import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { STATUS_HEX, personInitials, effectiveStatus, markerColor, UNASSIGNED_NEXT_DAY_HEX } from "@/lib/orderStatus";
import { regionCenter, boundsForPoints } from "@/lib/mapBounds";
import { useRegion } from "@/context/RegionContext";

// Auto-fit the map to the north/south/east/west extremes of the actual
// order + pharmacy pins — no regional baseline padding. This is the
// planning view: the dispatcher wants to see the exact cluster they're
// building a route out of, not the whole workspace.
function FitToPoints({ points, region }) {
  const map = useMap();
  useEffect(() => {
    const bounds = boundsForPoints(points || [], region);
    map.fitBounds(bounds, { padding: [24, 24], maxZoom: 15 });
  }, [points, map, region]);
  return null;
}

const pharmacyInitials = (name) => personInitials(name, "PH");

const pharmacyPin = (name) => L.divIcon({
  className: "",
  html: `<div style="background:#111;color:#fff;width:28px;height:28px;border-radius:6px;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:10px;letter-spacing:.3px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);">${pharmacyInitials(name)}</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});

const PICKUP_COLOR = "#7C3AED";

const selectablePin = (initials, color, isSelected) => L.divIcon({
  className: "",
  html: `<div style="
    position:relative;
    background:${isSelected ? color : "#ffffff"};
    color:${isSelected ? "#ffffff" : color};
    width:29px;height:29px;
    border-radius:50%;
    display:flex;align-items:center;justify-content:center;
    font-weight:800;font-size:10px;letter-spacing:.3px;
    border:3px solid ${color};
    box-shadow:0 4px 10px rgba(0,0,0,${isSelected ? 0.35 : 0.18});
    transition:all .15s ease;
  ">${initials}${isSelected ? `<span style="position:absolute;top:-6px;right:-6px;background:#234123;color:#fff;font-size:11px;width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #fff;">✓</span>` : ""}</div>`,
  iconSize: [29, 29], iconAnchor: [14, 14],
});

const selectablePickupPin = (initials, isSelected) => L.divIcon({
  className: "",
  html: `<div style="
    position:relative;
    background:${isSelected ? PICKUP_COLOR : "#f3e8ff"};
    color:${isSelected ? "#ffffff" : PICKUP_COLOR};
    width:29px;height:29px;
    border-radius:50%;
    display:flex;align-items:center;justify-content:center;
    font-weight:800;font-size:10px;letter-spacing:.3px;
    border:3px solid ${PICKUP_COLOR};
    box-shadow:0 4px 10px rgba(124,58,237,${isSelected ? 0.55 : 0.3});
    transition:all .15s ease;
  ">${initials}
    <span style="position:absolute;top:-7px;left:-7px;background:#4C1D95;color:#fff;font-size:9px;font-weight:900;width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;letter-spacing:0;">P</span>
    ${isSelected ? `<span style="position:absolute;top:-6px;right:-6px;background:#234123;color:#fff;font-size:11px;width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #fff;">✓</span>` : ""}
  </div>`,
  iconSize: [29, 29], iconAnchor: [14, 14],
});

/**
 * Map view for picking unassigned orders directly.
 * - Clicking a marker toggles its presence in `selected` Set.
 * - Pharmacy locations are rendered as black initial pins (read-only).
 */
export default function SelectableOrdersMap({
  orders = [],
  pharmacies = [],
  selected = new Set(),
  onToggle = () => {},
  height = 560,
}) {
  const { active: activeRegion } = useRegion();
  const geocoded = useMemo(() => orders.filter((o) => o.lat != null && o.lng != null), [orders]);
  const geoPharms = useMemo(() => pharmacies.filter((p) => p.lat != null && p.lng != null), [pharmacies]);

  const center = geocoded[0]
    ? [geocoded[0].lat, geocoded[0].lng]
    : geoPharms[0] ? [geoPharms[0].lat, geoPharms[0].lng] : regionCenter(activeRegion.id);

  // Build the fit target from ONLY the geocoded ORDERS — not pharmacies.
  // Including pharmacies would zoom out to include the pharmacy(ies) even
  // when the actual delivery cluster is elsewhere. The dispatcher wants
  // the frame tight on the stops they might select.
  const fitPoints = useMemo(
    () => geocoded.map((o) => [o.lat, o.lng]),
    [geocoded],
  );

  return (
    <div className="rounded-xl border border-border bg-card/80 overflow-hidden" data-testid="selectable-orders-map">
      <div className="rounded-t-xl overflow-hidden" style={{ height }}>
        <MapContainer center={center} zoom={10} zoomSnap={0} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          {geoPharms.map((p) => (
            <Marker key={p.id} position={[p.lat, p.lng]} icon={pharmacyPin(p.name)}>
              <Popup>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs">{p.address}</div>
              </Popup>
            </Marker>
          ))}
          {geocoded.map((o) => {
            const isSel = selected.has(o.id);
            const initials = personInitials(o.customer_name);
            const isPickup = (o.deliver_type || "").toLowerCase() === "pickup";
            // Color the pin red when this order has previously failed delivery
            // (failed_count > 0). Pickup keeps its purple square treatment.
            const prevFailed = (o.failed_count || 0) > 0 && o.status !== "delivered";
            const st = effectiveStatus(o);
            const pinColor = prevFailed ? STATUS_HEX.failed : markerColor(o, st);
            return (
              <Marker
                key={o.id}
                position={[o.lat, o.lng]}
                icon={isPickup ? selectablePickupPin(initials, isSel) : selectablePin(initials, pinColor, isSel)}
                eventHandlers={{ click: () => onToggle(o.id) }}
              >
                <Popup>
                  {(o.pharmacy_nickname || o.pharmacy_name) && (
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5" data-testid={`overview-popup-pharmacy-${o.id}`}>
                      {o.pharmacy_nickname || o.pharmacy_name}
                    </div>
                  )}
                  <div className="font-medium">{o.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{o.address}</div>
                  {prevFailed && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                      {(o.failed_count || 0) >= 2 ? "Failed 2× — return to pharmacy" : "Previously failed"}
                    </div>
                  )}
                  {isPickup && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#fef3c7", color: "#92400e" }}>
                      Pick Up
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => onToggle(o.id)}
                    className="mt-2 text-xs font-medium rounded-full px-2.5 py-1 border border-primary text-primary hover:bg-primary hover:text-white transition-colors"
                    data-testid={`selectable-toggle-${o.id}`}
                  >
                    {isSel ? "Remove from selection" : "Add to selection"}
                  </button>
                </Popup>
              </Marker>
            );
          })}
          <FitToPoints points={fitPoints} region={activeRegion.id} />
        </MapContainer>
      </div>
      <div className="px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-border text-xs">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: "#111" }} />Pharmacy</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_HEX.pending }} />Unassigned (same day)</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: UNASSIGNED_NEXT_DAY_HEX }} />Unassigned (next day)</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-offset-2" style={{ background: "#fff", borderColor: STATUS_HEX.pending, ["--tw-ring-color"]: STATUS_HEX.pending }} />Selected</span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-failed"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-offset-2" style={{ background: "#fff", borderColor: STATUS_HEX.failed, ["--tw-ring-color"]: STATUS_HEX.failed }} />Failed (retry)</span>
        <span className="inline-flex items-center gap-1.5" data-testid="legend-pickup"><span className="h-2.5 w-2.5 rounded-full border-2" style={{ background: "#f3e8ff", borderColor: PICKUP_COLOR }} />Pick Up order</span>
        <span className="ml-auto text-muted-foreground"><span className="font-mono-num font-semibold text-foreground">{geocoded.length}</span> unassigned · <span className="font-mono-num font-semibold text-foreground">{selected.size}</span> selected</span>
      </div>
    </div>
  );
}
